import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TAG_COLOR_PALETTE, DEFAULT_TAG_COLOR } from '../../features/tags';
import styles from './TagColorPicker.module.css';

type Props = {
  value: string | null | undefined;
  onChange: (color: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
};

type PopoverPosition = { top: number; left: number };

export function TagColorPicker({ value, onChange, disabled, ariaLabel }: Props) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // トリガー位置に合わせてポップオーバー座標を計算
  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({ top: rect.bottom + 6, left: rect.left });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScrollOrResize = () => updatePosition();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const currentColor = value || DEFAULT_TAG_COLOR;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        style={{ backgroundColor: currentColor }}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-label={ariaLabel ?? 'カラーを選択'}
        aria-haspopup="dialog"
        aria-expanded={open}
      />
      {open && position &&
        createPortal(
          <div
            ref={popoverRef}
            className={styles.popover}
            role="dialog"
            style={{ top: position.top, left: position.left }}
          >
            <div className={styles.grid}>
              {TAG_COLOR_PALETTE.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  className={`${styles.swatch} ${currentColor === color.value ? styles.swatchSelected : ''}`}
                  style={{ backgroundColor: color.value }}
                  onClick={() => {
                    onChange(color.value);
                    setOpen(false);
                  }}
                  title={color.name}
                  aria-label={color.name}
                />
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
